import { supabase } from './supabase'
import {
  learningContentKey,
  learningStatusTimestamps,
  resolveExplicitLearningState,
  resolvePassiveLearningState,
  type LearningContentLocator,
  type LearningContentType,
  type LearningStatus
} from './learning-state-core'

export type LearningContentState = {
  id: string
  user_id: string
  content_type: LearningContentType
  content_id: string
  status: LearningStatus
  familiarity: number | null
  progress: number | null
  metadata: Record<string, unknown>
  first_seen_at: string | null
  last_seen_at: string | null
  saved_at: string | null
  learned_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

export type LearningContentStateInput = LearningContentLocator & {
  userId: string
  status: LearningStatus
  familiarity?: number | null
  progress?: number | null
  metadata?: Record<string, unknown>
}

export type PassiveLearningContentStateInput = LearningContentLocator & {
  status: LearningStatus
  familiarity?: number | null
  progress?: number | null
  metadata?: Record<string, unknown>
}

function groupLocators(locators: LearningContentLocator[]) {
  const groups = new Map<LearningContentType, string[]>()
  for (const locator of locators) {
    if (!locator.contentId) continue
    const current = groups.get(locator.contentType) ?? []
    current.push(locator.contentId)
    groups.set(locator.contentType, Array.from(new Set(current)))
  }
  return groups
}

export async function getLearningContentStates(
  locators: LearningContentLocator[]
): Promise<Map<string, LearningContentState>> {
  const groups = groupLocators(locators)
  if (!groups.size) return new Map()

  const rows = await Promise.all(
    Array.from(groups.entries()).map(async ([contentType, ids]) => {
      const { data, error } = await supabase
        .from('learning_content_states')
        .select('*')
        .eq('content_type', contentType)
        .in('content_id', ids)

      if (error) throw error
      return (data ?? []) as LearningContentState[]
    })
  )

  return new Map(
    rows.flat().map((row) => [
      learningContentKey({
        contentType: row.content_type,
        contentId: row.content_id
      }),
      row
    ])
  )
}

export async function getLearningContentState(
  locator: LearningContentLocator
): Promise<LearningContentState | null> {
  return (
    (await getLearningContentStates([locator])).get(
      learningContentKey(locator)
    ) ?? null
  )
}

export async function upsertLearningContentState({
  userId,
  contentType,
  contentId,
  status,
  familiarity,
  progress,
  metadata = {}
}: LearningContentStateInput): Promise<LearningContentState> {
  const existing = await getLearningContentState({ contentType, contentId })
  const resolved = resolvePassiveLearningState(existing, {
    status,
    familiarity,
    progress
  })
  const timestamps = learningStatusTimestamps(status)

  const { data, error } = await supabase
    .from('learning_content_states')
    .upsert(
      {
        user_id: userId,
        content_type: contentType,
        content_id: contentId,
        status: resolved.status,
        familiarity: resolved.familiarity,
        progress: resolved.progress,
        metadata: { ...(existing?.metadata ?? {}), ...metadata },
        first_seen_at:
          existing?.first_seen_at ?? timestamps.first_seen_at ?? null,
        last_seen_at: timestamps.last_seen_at ?? existing?.last_seen_at ?? null,
        saved_at: existing?.saved_at ?? timestamps.saved_at ?? null,
        learned_at: existing?.learned_at ?? timestamps.learned_at ?? null,
        completed_at: existing?.completed_at ?? timestamps.completed_at ?? null
      },
      { onConflict: 'user_id,content_type,content_id' }
    )
    .select()
    .single()

  if (error) throw error
  return data as LearningContentState
}

export async function upsertPassiveLearningContentStates({
  userId,
  items
}: {
  userId: string
  items: PassiveLearningContentStateInput[]
}): Promise<void> {
  const uniqueItems = Array.from(
    new Map(
      items
        .filter((item) => item.contentId)
        .map((item) => [learningContentKey(item), item])
    ).values()
  )
  if (!uniqueItems.length) return

  const existingStates = await getLearningContentStates(uniqueItems)
  const rows = uniqueItems.map((item) => {
    const existing = existingStates.get(learningContentKey(item))
    const resolved = resolvePassiveLearningState(existing, {
      status: item.status,
      familiarity: item.familiarity,
      progress: item.progress
    })
    const timestamps = learningStatusTimestamps(item.status)

    return {
      user_id: userId,
      content_type: item.contentType,
      content_id: item.contentId,
      status: resolved.status,
      familiarity: resolved.familiarity,
      progress: resolved.progress,
      metadata: { ...(existing?.metadata ?? {}), ...(item.metadata ?? {}) },
      first_seen_at:
        existing?.first_seen_at ?? timestamps.first_seen_at ?? null,
      last_seen_at: timestamps.last_seen_at ?? existing?.last_seen_at ?? null,
      saved_at: existing?.saved_at ?? timestamps.saved_at ?? null,
      learned_at: existing?.learned_at ?? timestamps.learned_at ?? null,
      completed_at: existing?.completed_at ?? timestamps.completed_at ?? null
    }
  })

  for (let index = 0; index < rows.length; index += 200) {
    const { error } = await supabase
      .from('learning_content_states')
      .upsert(rows.slice(index, index + 200), {
        onConflict: 'user_id,content_type,content_id'
      })

    if (error) throw error
  }
}

export async function setLearningContentState(
  input: LearningContentStateInput
): Promise<LearningContentState> {
  const { userId, contentType, contentId, status, metadata = {} } = input
  const existing = await getLearningContentState({ contentType, contentId })
  const next = {
    status,
    ...('familiarity' in input ? { familiarity: input.familiarity } : {}),
    ...('progress' in input ? { progress: input.progress } : {})
  }
  const resolved = resolveExplicitLearningState(existing, next)

  const { data, error } = await supabase
    .from('learning_content_states')
    .upsert(
      {
        user_id: userId,
        content_type: contentType,
        content_id: contentId,
        status: resolved.status,
        familiarity: resolved.familiarity,
        progress: resolved.progress,
        metadata: { ...(existing?.metadata ?? {}), ...metadata },
        first_seen_at: resolved.first_seen_at,
        last_seen_at: resolved.last_seen_at,
        saved_at: resolved.saved_at,
        learned_at: resolved.learned_at,
        completed_at: resolved.completed_at
      },
      { onConflict: 'user_id,content_type,content_id' }
    )
    .select()
    .single()

  if (error) throw error
  return data as LearningContentState
}

export {
  clampLearningValue,
  learningContentKey,
  mergeLearningProgress,
  mergeLearningStatus,
  resolveExplicitLearningState,
  resolvePassiveLearningState
} from './learning-state-core'
export type {
  LearningContentLocator,
  LearningContentType,
  LearningStatus
} from './learning-state-core'
